import { Calendar, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

const Hero = () => {
  const { t } = useLanguage();
  
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary/5 via-background to-primary/10">
      {/* Bavarian Pattern Background */}
      <div className="absolute inset-0 bavarian-pattern" />

      <div className="container mx-auto px-4 relative z-10 pt-24 md:pt-16">
        <div className="max-w-5xl mx-auto text-center space-y-8">
          {/* Main Heading */}
          <div className="space-y-4 animate-fade-in">
            <h1 className="text-4xl md:text-7xl lg:text-8xl font-serif font-bold leading-tight">
              <span className="text-primary">
                Quetschnmanfred
              </span>
              <span className="block text-3xl md:text-4xl text-foreground mt-4">
                Manfred Hahnel
              </span>
              <span className="block text-2xl md:text-3xl text-foreground/70 mt-2">
                {t.hero.subtitle}
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-foreground/80 max-w-3xl mx-auto leading-relaxed font-medium">
              {t.hero.description}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
            <Button
              size="lg"
              onClick={scrollToContact}
              className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 group"
            >
              {t.hero.bookEvent}
              <Calendar className="ml-2 w-5 h-5 group-hover:rotate-12 transition-transform" />
            </Button>
            
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                const element = document.getElementById('gallery');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-lg px-8 py-6 rounded-full border-2 hover:bg-accent/10"
            >
              {t.hero.viewGallery}
              <PartyPopper className="ml-2 w-5 h-5" />
            </Button>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-12">
            {[
              { ...t.hero.features.weddings, gradient: 'from-primary/30 to-primary/10', border: 'border-primary/50' },
              { ...t.hero.features.traditional, gradient: 'from-accent/30 to-accent/10', border: 'border-accent/50' },
              { ...t.hero.features.venues, gradient: 'from-primary/20 to-accent/20', border: 'border-primary/50' },
            ].map((feature, index) => (
              <div
                key={index}
                className={`bg-gradient-to-br ${feature.gradient} backdrop-blur-sm border-2 ${feature.border} rounded-2xl p-6 hover:shadow-2xl transition-all hover:scale-105 group`}
              >
                <h3 className="text-lg font-bold mb-2 text-foreground group-hover:text-primary transition-all">
                  {feature.title}
                </h3>
                <p className="text-foreground/70 text-sm font-medium">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-primary rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-primary rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
